import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, FileText, Star } from "lucide-react";
import { COMPANY } from "../data/site";
import {
  useGoogleReviews,
  formatRatingSv,
  formatCountSv,
} from "../lib/googleReviews";

/** Official multi-color Google "G" mark — inline so the badge has no remote dep. */
function GoogleG({ className = "w-3.5 h-3.5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

const NAV_PRIMARY = [
  { to: "/", label: "Hem" },
];

const ALL_SERVICES = [
  { to: "/tradfallning", label: "Trädfällning" },
  { to: "/beskarning", label: "Beskärning" },
  { to: "/stubbfrasning", label: "Stubbfräsning" },
  { to: "/bortforsling", label: "Bortforsling" },
  { to: "/mark-och-anlaggning", label: "Mark & anläggning" },
  { to: "/gronyteskotsel-och-hackklippning", label: "Grönyteskötsel & häckklippning" },
  { to: "/snorojning", label: "Snöröjning" },
  { to: "/motorsagskorkort", label: "Motorsågskörkort A+B" },
];

const NAV_SECONDARY = [
  { to: "/referenser", label: "Referenser" },
  { to: "/blogg", label: "Blogg" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const google = useGoogleReviews();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const navLinkClass = ({ isActive }) =>
    `relative font-heading text-[14px] font-semibold transition-colors px-1 py-2 ${
      isActive
        ? "text-[#1B2A1E]"
        : "text-[#3a4338] hover:text-[#D95D2E]"
    }`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 w-full border-b transition-all ${
          scrolled
            ? "bg-[#F7F5F0]/95 backdrop-blur-md border-[#E2E0D8] shadow-sm"
            : "bg-[#F7F5F0] border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <Link
              to="/"
              data-testid="header-logo-link"
              className="flex items-center gap-2.5 group"
            >
              <img
                src="/images/logo-mark-transparent.png"
                alt="Trädfällarna Laholm AB"
                className="h-10 lg:h-11 w-auto object-contain"
              />
              <span className="hidden sm:block leading-tight">
                <span className="block font-heading font-extrabold text-[#1B2A1E] text-[15px] tracking-tight">
                  Trädfällarna
                </span>
                <span className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6258] font-semibold">
                  Laholm AB
                </span>
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Huvudmeny">
              {NAV_PRIMARY.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={navLinkClass}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Tjänster — scrollar till tjänstesektionen på startsidan */}
              <Link
                to="/#tjanster"
                data-testid="nav-services-link"
                className="font-heading text-[14px] font-semibold text-[#3a4338] hover:text-[#D95D2E] py-2 transition-colors"
              >
                Tjänster
              </Link>

              {NAV_SECONDARY.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={navLinkClass}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Phone / CTA: reserve review-badge width to prevent nav shift when it loads */}
            <div className="flex items-center gap-2 xl:min-w-[360px] xl:justify-end">
              {google.hasData && (
                <Link
                  to="/#recensioner"
                  data-testid="header-google-rating"
                  aria-label={`${formatRatingSv(google.rating)} av 5 stjärnor på Google — ${formatCountSv(google.ratingCount)} omdömen`}
                  className="inline-flex items-center gap-1 sm:gap-1.5 bg-white border border-[#E2E0D8] hover:border-[#D95D2E] hover:bg-[#FFF6F0] rounded-full px-2 sm:px-2.5 py-1 sm:py-1.5 transition-colors group"
                >
                  <GoogleG className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                  <span className="flex items-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-[#D95D2E] text-[#D95D2E]"
                        strokeWidth={1.5}
                      />
                    ))}
                  </span>
                  <span
                    data-testid="header-google-rating-value"
                    className="font-heading text-[11px] sm:text-[12px] font-extrabold text-[#1B2A1E]"
                  >
                    {formatRatingSv(google.rating)}/5
                  </span>
                  <span
                    data-testid="header-google-rating-count"
                    className="text-[10px] sm:text-[11px] font-semibold text-[#5A6258] group-hover:text-[#D95D2E]"
                  >
                    ({formatCountSv(google.ratingCount)})
                  </span>
                </Link>
              )}
              <Link
                to="/kontakt#offert"
                data-testid="header-quote-button"
                className="hidden sm:inline-flex items-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold text-sm px-3.5 lg:px-4 py-2 rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" strokeWidth={2.4} />
                <span>Få offert</span>
              </Link>

              {/* Mobile hamburger */}
              <button
                type="button"
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-[#1B2A1E] hover:bg-[#EFEBDF]"
                aria-label="Öppna meny"
                aria-expanded={open}
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 z-50 bg-[#F7F5F0] overflow-y-auto pt-20 pb-32"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="mobile-menu-close"
            className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-md text-[#1B2A1E] hover:bg-[#EFEBDF]"
            aria-label="Stäng meny"
          >
            <X className="w-6 h-6" />
          </button>
          <nav className="px-6 flex flex-col gap-1" aria-label="Mobilmeny">
            {NAV_PRIMARY.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-3.5 border-b border-[#E2E0D8] font-heading text-lg font-bold ${
                    isActive ? "text-[#D95D2E]" : "text-[#1B2A1E]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-4 pb-2 text-xs uppercase tracking-[0.18em] font-bold text-[#5A6258]">
              Tjänster
            </div>
            {ALL_SERVICES.map((s) => (
              <NavLink
                key={s.to}
                to={s.to}
                data-testid={`mobile-nav-${s.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `block py-3 border-b border-[#E2E0D8] font-heading text-base font-semibold ${
                    isActive ? "text-[#D95D2E]" : "text-[#18221A]"
                  }`
                }
              >
                {s.label}
              </NavLink>
            ))}
            <div className="pt-4" />
            {NAV_SECONDARY.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-3.5 border-b border-[#E2E0D8] font-heading text-lg font-bold ${
                    isActive ? "text-[#D95D2E]" : "text-[#1B2A1E]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-8 grid grid-cols-2 gap-3">
              <a
                href={`tel:${COMPANY.phoneE164}`}
                data-testid="mobile-menu-call"
                className="btn-primary"
              >
                <Phone className="w-4 h-4" /> Ring oss
              </a>
              <Link
                to="/kontakt#offert"
                data-testid="mobile-menu-quote"
                className="btn-secondary"
              >
                Få offert
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
